import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Phone, Globe, Shield, Zap, HeadphonesIcon, TrendingUp,
  ArrowRight, Check, MessageCircle, Users, Server, Award,
  PhoneCall, CreditCard, Settings, Star, Wifi, MonitorSmartphone
} from "lucide-react";
import { Button } from "@/components/ui/button";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};
const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const HeroVisual = () => (
  <div className="relative">
    <div className="bg-slate-900 rounded-3xl overflow-hidden shadow-2xl min-h-[440px] flex items-center justify-center relative">
      {/* Background grid lines */}
      <svg className="absolute inset-0 w-full h-full opacity-10" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#E86B6B" strokeWidth="0.5"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />
      </svg>

      {/* Glow circles */}
      <div className="absolute top-8 right-8 w-48 h-48 bg-brand-500/15 rounded-full blur-3xl" />
      <div className="absolute bottom-8 left-8 w-32 h-32 bg-brand-400/10 rounded-full blur-2xl" />

      {/* Central phone ring visual */}
      <div className="relative z-10 flex flex-col items-center justify-center gap-6 p-10">
        <div className="relative">
          <div className="absolute inset-0 rounded-full bg-brand-500/20 animate-ping" style={{ animationDuration: '2.5s' }} />
          <div className="absolute -inset-4 rounded-full border border-brand-500/20" />
          <div className="absolute -inset-8 rounded-full border border-brand-500/10" />
          <div className="w-24 h-24 bg-gradient-to-br from-brand-500 to-brand-600 rounded-full flex items-center justify-center shadow-[0_0_40px_rgba(232,107,107,0.4)]">
            <PhoneCall className="w-11 h-11 text-white" />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-2">
          {[
            { label: "235+", sub: "Countries" },
            { label: "99.9%", sub: "Uptime" },
            { label: "53K+", sub: "Routes" }
          ].map((s, i) => (
            <div key={i} className="text-center bg-white/5 border border-white/10 rounded-xl px-4 py-3">
              <p className="text-xl font-bold text-brand-400">{s.label}</p>
              <p className="text-xs text-slate-400">{s.sub}</p>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Floating card */}
    <motion.div
      className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3"
      animate={{ y: [0, -8, 0] }}
      transition={{ duration: 3, repeat: Infinity }}
    >
      <div className="w-10 h-10 bg-brand-50 rounded-full flex items-center justify-center">
        <Globe className="w-5 h-5 text-brand-500" />
      </div>
      <div>
        <p className="text-base font-bold text-slate-900">235+ Countries</p>
        <p className="text-xs text-slate-500">A-Z Termination Live</p>
      </div>
    </motion.div>

    <motion.div
      className="absolute -top-5 -right-5 bg-slate-900 text-white rounded-2xl shadow-xl p-4 flex items-center gap-3"
      animate={{ y: [0, 8, 0] }}
      transition={{ duration: 3.5, repeat: Infinity }}
    >
      <div className="w-10 h-10 bg-brand-500/20 rounded-full flex items-center justify-center">
        <Zap className="w-5 h-5 text-brand-400" />
      </div>
      <div>
        <p className="text-base font-bold">Free Trial</p>
        <p className="text-xs text-slate-400">Credits on signup</p>
      </div>
    </motion.div>
  </div>
);

const features = [
  {
    icon: Globe,
    title: "A-Z Termination",
    description: "Reach any destination worldwide. We cover 235+ countries with 53,000+ voice routes through Tier 1 and direct carrier partnerships."
  },
  {
    icon: CreditCard,
    title: "Free Trial Credits",
    description: "Start calling immediately. New accounts receive free trial credits so you can test quality and rates before committing."
  },
  {
    icon: TrendingUp,
    title: "Cheapest Rates",
    description: "Industry-leading rates starting from $0.001/min. Real-time rate updates with no hidden charges or setup fees."
  },
  {
    icon: MonitorSmartphone,
    title: "On-Site Web Dialer",
    description: "Make calls directly from your browser via our built-in WebRTC dialer — no extra software or configuration needed."
  },
  {
    icon: Settings,
    title: "Easy Onboarding",
    description: "Get connected in minutes. Register, deposit credits, and configure your SIP client or use our web dialer right away."
  },
  {
    icon: Shield,
    title: "Full CLI Delivery",
    description: "Premium plans guarantee full Caller Line Identification delivery with Gold and Platinum routes via Tier 1 partnerships."
  }
];

const steps = [
  {
    number: "01",
    icon: Users,
    title: "Create Free Account",
    description: "Sign up in seconds at voiphub.in. No credit card required to register. Your account is ready instantly."
  },
  {
    number: "02",
    icon: CreditCard,
    title: "Add Credits & Pick a Plan",
    description: "Choose Silver, Gold, or Platinum based on your quality needs. Minimum deposit $100 USD. Free trial credits on first top-up."
  },
  {
    number: "03",
    icon: PhoneCall,
    title: "Start Making Calls",
    description: "Point your SIP client to sip.voiphub.in or use our built-in browser dialer. Full CLI, low PDD, high ASR guaranteed."
  }
];

const planHighlights = [
  {
    name: "Silver",
    prefix: "10003",
    tagline: "Price-Oriented",
    color: "from-slate-500 to-slate-700",
    features: ["Competitive pricing", "Stable connections", "Continuous monitoring"]
  },
  {
    name: "Gold",
    prefix: "10001",
    tagline: "Quality-Focused",
    color: "from-brand-500 to-brand-700",
    badge: "Most Popular",
    features: ["Full CLI delivery", "High ASR & ACD", "Low PDD", "Priority support"]
  },
  {
    name: "Platinum",
    prefix: "10002",
    tagline: "Highest Quality",
    color: "from-indigo-500 to-indigo-700",
    features: ["Tier 1 direct routes", "Premium CLI", "24/7 VIP support", "Dedicated routes"]
  }
];

const HomePage = () => {
  return (
    <div data-testid="home-page">
      {/* ── Hero ──────────────────────────────────────────── */}
      <section
        className="relative min-h-[92vh] flex items-center overflow-hidden"
        style={{ background: "linear-gradient(135deg, #fff 0%, #fff5f5 60%, #ffeaea 100%)" }}
        data-testid="hero-section"
      >
        <div className="absolute top-16 right-0 w-[500px] h-[500px] bg-brand-400/8 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-brand-400/5 rounded-full blur-3xl pointer-events-none" />

        <div className="container-custom relative z-10 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <motion.div
              className="lg:col-span-6"
              initial="hidden"
              animate="visible"
              variants={stagger}
            >
              <motion.div
                variants={fadeInUp}
                className="inline-flex items-center gap-2 bg-brand-50 border border-brand-100 text-brand-600 px-4 py-2 rounded-full text-sm font-semibold mb-6"
              >
                <Zap className="w-4 h-4" />
                Premium A-Z Voice Termination
              </motion.div>

              <motion.h1
                variants={fadeInUp}
                className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-slate-900 leading-[1.08] mb-6"
              >
                Global VoIP<br />
                <span className="text-brand-500">Termination</span><br />
                Made Simple
              </motion.h1>

              <motion.p
                variants={fadeInUp}
                className="text-lg md:text-xl text-slate-600 mb-8 max-w-lg leading-relaxed"
              >
                Connect to <strong>235+ countries</strong> with crystal-clear voice quality.
                Industry-lowest rates, full CLI delivery, high ASR/ACD — and a
                built-in <strong>browser dialer</strong> so you can call from anywhere.
              </motion.p>

              <motion.div variants={fadeInUp} className="flex flex-wrap gap-4 mb-10">
                <a href="/portal/">
                  <Button
                    className="bg-brand-500 hover:bg-brand-600 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-brand-400/30 transition-all"
                    data-testid="hero-signup-btn"
                  >
                    Start Free Trial <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </a>
                <Link to="/rates">
                  <Button
                    variant="outline"
                    className="rounded-full px-8 py-6 text-lg font-medium border-slate-300 hover:border-brand-400 hover:text-brand-500"
                    data-testid="hero-rates-btn"
                  >
                    View Live Rates
                  </Button>
                </Link>
              </motion.div>

              <motion.div variants={fadeInUp} className="flex flex-wrap gap-5">
                {[
                  { icon: Shield, text: "Full CLI Delivery" },
                  { icon: Zap, text: "Low PDD" },
                  { icon: TrendingUp, text: "High ASR/ACD" },
                  { icon: Award, text: "24/7 Support" }
                ].map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-slate-600">
                    <f.icon className="w-5 h-5 text-brand-400" />
                    <span className="font-medium text-sm">{f.text}</span>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            <motion.div
              className="lg:col-span-6"
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.25 }}
            >
              <HeroVisual />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Stats bar ─────────────────────────────────────── */}
      <section className="bg-slate-900 py-10 noise-bg" data-testid="stats-section">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { label: "Voice Routes", value: "53,000+" },
              { label: "Countries Covered", value: "235+" },
              { label: "Active Customers", value: "500+" },
              { label: "Network Uptime", value: "99.9%" }
            ].map((stat, i) => (
              <motion.div
                key={i}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <p className="text-3xl md:text-4xl font-bold text-brand-400">{stat.value}</p>
                <p className="text-slate-400 mt-1 text-sm">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 6-feature grid ────────────────────────────────── */}
      <section className="section-light" data-testid="features-section">
        <div className="container-custom">
          <motion.div
            className="text-center mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Everything You Need to Terminate Calls Globally
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From free trial credits to on-site browser dialing — VoipHub is built for operators who need quality, speed, and simplicity.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <motion.div
                key={i}
                className="feature-card group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                viewport={{ once: true }}
              >
                <div className="w-14 h-14 bg-brand-50 rounded-xl flex items-center justify-center mb-5 group-hover:bg-brand-500 transition-colors">
                  <feature.icon className="w-7 h-7 text-brand-500 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ──────────────────────────────────── */}
      <section className="section-alt" data-testid="onboarding-section">
        <div className="container-custom">
          <motion.div
            className="text-center mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 bg-brand-50 text-brand-600 px-4 py-2 rounded-full text-sm font-semibold mb-5">
              <Settings className="w-4 h-4" />
              Easy Onboarding — 3 Simple Steps
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Start Calling in Under 5 Minutes
            </h2>
            <p className="text-lg text-slate-600 max-w-xl mx-auto">
              No long contracts, no hardware, no complicated setup. Register and start terminating global calls today.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 relative">
            {/* connector line */}
            <div className="hidden md:block absolute top-10 left-1/6 right-1/6 h-px bg-gradient-to-r from-transparent via-brand-300 to-transparent" />

            {steps.map((step, i) => (
              <motion.div
                key={i}
                className="relative text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.15 }}
                viewport={{ once: true }}
              >
                <div className="w-20 h-20 bg-white rounded-2xl shadow-lg flex items-center justify-center mx-auto mb-6 border border-slate-100">
                  <step.icon className="w-9 h-9 text-brand-500" />
                </div>
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-500 text-white text-xs font-bold w-7 h-7 rounded-full flex items-center justify-center">
                  {step.number}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
                <p className="text-slate-600 leading-relaxed">{step.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <a href="/portal/">
              <Button className="bg-brand-500 hover:bg-brand-600 text-white rounded-full px-8 py-5 text-base font-semibold shadow-lg">
                Create Your Free Account <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* ── Plans overview ────────────────────────────────── */}
      <section className="section-light" data-testid="plans-section">
        <div className="container-custom">
          <motion.div
            className="text-center mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Three Plans. One SIP Host.
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              All plans connect to <strong>sip.voiphub.in</strong> — just change the tech prefix to select your quality tier.
              No separate credentials needed.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {planHighlights.map((plan, i) => (
              <motion.div
                key={i}
                className="relative rounded-2xl overflow-hidden shadow-lg"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                {plan.badge && (
                  <div className="absolute top-4 right-4 bg-white text-brand-500 text-xs font-bold px-3 py-1 rounded-full shadow">
                    {plan.badge}
                  </div>
                )}
                <div className={`bg-gradient-to-br ${plan.color} p-6 text-white`}>
                  <h3 className="text-2xl font-bold mb-1">{plan.name} IP Voice</h3>
                  <p className="text-white/80 text-sm mb-4">{plan.tagline}</p>
                  <div className="bg-white/15 rounded-xl p-3 inline-block">
                    <p className="text-xs text-white/70 uppercase tracking-wider">Tech Prefix (Dial Code)</p>
                    <p className="text-3xl font-mono font-bold mt-1">{plan.prefix}</p>
                  </div>
                </div>
                <div className="bg-white p-5">
                  <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-3">SIP Host</p>
                  <p className="text-slate-800 font-mono text-sm bg-slate-50 rounded-lg px-3 py-2 mb-5">sip.voiphub.in</p>
                  <ul className="space-y-2">
                    {plan.features.map((f, j) => (
                      <li key={j} className="flex items-center gap-2 text-slate-700 text-sm">
                        <Check className="w-4 h-4 text-brand-500 flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Link to="/pricing" className="block mt-5">
                    <Button variant="outline" className="w-full rounded-full border-slate-200 hover:border-brand-400 hover:text-brand-500 text-sm">
                      See Full {plan.name} Details <ArrowRight className="ml-1 w-3 h-3" />
                    </Button>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="bg-slate-50 border border-slate-100 rounded-2xl p-6 text-center">
            <p className="text-slate-600 text-sm">
              <strong>SIP Server:</strong> sip.voiphub.in &nbsp;·&nbsp;
              <strong>WebRTC/WSS:</strong> wss://sip.voiphub.in/ws &nbsp;·&nbsp;
              <strong>Free trial credits</strong> included on first account top-up
            </p>
          </div>
        </div>
      </section>

      {/* ── Browser Dialer highlight ──────────────────────── */}
      <section className="section-alt" data-testid="dialer-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 bg-brand-50 text-brand-600 px-4 py-2 rounded-full text-sm font-semibold mb-5">
                <MonitorSmartphone className="w-4 h-4" />
                Built-In Browser Dialer
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                Call From Your Browser — No App Needed
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                VoipHub includes a fully functional <strong>WebRTC softphone</strong> inside your portal.
                Open the dialer, select your SIP account, and make calls directly from Chrome or any modern browser.
                No PortSIP, Zoiper, or external software required.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  "WebRTC-powered, no plugin required",
                  "Connects via wss://sip.voiphub.in/ws",
                  "Call history & DTMF keypad built-in",
                  "Works on desktop and mobile browsers",
                  "Auto-reconnects on network drops"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700">
                    <Check className="w-5 h-5 text-brand-500 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <a href="/portal/">
                <Button className="bg-brand-500 hover:bg-brand-600 text-white rounded-full px-6 py-5 font-semibold">
                  Open Portal & Try It <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-slate-900 rounded-3xl p-8 shadow-2xl relative overflow-hidden">
                <div className="absolute inset-0 opacity-5">
                  <svg width="100%" height="100%">
                    <defs>
                      <pattern id="dots" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                        <circle cx="2" cy="2" r="1.5" fill="#E86B6B"/>
                      </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#dots)"/>
                  </svg>
                </div>
                <div className="relative z-10">
                  {/* Simulated dialer UI */}
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs text-slate-400 uppercase tracking-wider">Browser Dialer</span>
                      <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full font-medium">● Online</span>
                    </div>
                    <div className="bg-white/8 rounded-xl p-3 font-mono text-2xl text-white tracking-widest text-center mb-4">
                      +91 9999 ×× ××××
                    </div>
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      {["1","2","3","4","5","6","7","8","9","*","0","#"].map(d => (
                        <div key={d} className="h-10 bg-white/8 border border-white/10 rounded-lg flex items-center justify-center text-white font-semibold text-sm">{d}</div>
                      ))}
                    </div>
                    <div className="flex gap-3">
                      <div className="flex-1 bg-green-500 rounded-xl h-11 flex items-center justify-center">
                        <PhoneCall className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 bg-red-500 rounded-xl h-11 flex items-center justify-center">
                        <Phone className="w-5 h-5 text-white" />
                      </div>
                    </div>
                  </div>
                  <p className="text-slate-400 text-xs text-center">wss://sip.voiphub.in/ws · Registered as voiphub1-web</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Why VoipHub ───────────────────────────────────── */}
      <section className="section-light" data-testid="why-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                Why Choose <span className="text-brand-500">VoipHub</span>?
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                We deliver wholesale VoIP termination backed by Tier 1 carrier partnerships. Whether you run a call center,
                a telecoms reseller, or just need affordable international calling, VoipHub has you covered.
              </p>
              <ul className="space-y-4">
                {[
                  "A-Z termination to 235+ countries on one platform",
                  "Rates starting from $0.001/min — updated in real time",
                  "Free trial credits on first account top-up",
                  "Web dialer, SIP/Zoiper, PortSIP — all supported",
                  "24/7 technical support via Telegram",
                  "No minimum commitment, no setup fees",
                  "Prepaid system — total cost control",
                  "Multiple payment options: Bitcoin, USDT, OXAPay"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-brand-50 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-brand-500" />
                    </div>
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="flex gap-4 mt-10">
                <Link to="/pricing">
                  <Button className="bg-brand-500 hover:bg-brand-600 text-white rounded-full px-6 py-5" data-testid="why-pricing-btn">
                    Compare Plans <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <a href="https://t.me/voiphub_support" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="rounded-full px-6 py-5 border-slate-300" data-testid="why-telegram-btn">
                    <MessageCircle className="mr-2 w-4 h-4" /> Chat on Telegram
                  </Button>
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-slate-900 rounded-3xl p-10 shadow-2xl">
                <div className="grid grid-cols-2 gap-5">
                  {[
                    { icon: Globe, title: "235+ Countries", sub: "A-Z coverage" },
                    { icon: TrendingUp, title: "53K+ Routes", sub: "Voice routes live" },
                    { icon: Wifi, title: "99.9% Uptime", sub: "SLA guaranteed" },
                    { icon: Star, title: "500+ Customers", sub: "Active operators" },
                    { icon: Zap, title: "Free Trial", sub: "Credits on signup" },
                    { icon: HeadphonesIcon, title: "24/7 Support", sub: "Via Telegram" }
                  ].map((item, i) => (
                    <motion.div
                      key={i}
                      className="bg-white/5 border border-white/8 rounded-xl p-4 flex items-center gap-3"
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      transition={{ delay: i * 0.07 }}
                      viewport={{ once: true }}
                    >
                      <div className="w-10 h-10 bg-brand-500/15 rounded-lg flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-5 h-5 text-brand-400" />
                      </div>
                      <div>
                        <p className="text-white font-semibold text-sm">{item.title}</p>
                        <p className="text-slate-400 text-xs">{item.sub}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────── */}
      <section className="section-dark" data-testid="cta-section">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Start Terminating Calls?
            </h2>
            <p className="text-lg text-slate-300 mb-4 max-w-2xl mx-auto">
              Join 500+ customers worldwide. Get free trial credits, connect in minutes, and terminate calls to 235+ countries at the industry's lowest rates.
            </p>
            <p className="text-sm text-brand-400 font-semibold mb-10">
              🎁 Free trial credits on your first account top-up · No setup fees · Cancel anytime
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/portal/">
                <Button
                  className="bg-brand-500 hover:bg-brand-600 text-white rounded-full px-10 py-6 text-lg font-semibold shadow-lg shadow-brand-500/30"
                  data-testid="cta-signup-btn"
                >
                  Create Free Account <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
              <Link to="/contact">
                <Button
                  variant="outline"
                  className="rounded-full px-10 py-6 text-lg font-medium border-slate-600 text-white hover:bg-white/10"
                  data-testid="cta-contact-btn"
                >
                  Contact Sales
                </Button>
              </Link>
            </div>
            <p className="text-slate-500 mt-8 text-sm">
              Minimum deposit: $100 USD · Prepaid, no contracts · SIP host: sip.voiphub.in
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
